import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShopMap = ({ 
  shops = [], 
  selectedShop = null, 
  onShopSelect = () => {},
  userLocation = null,
  searchRadius = 10,
  onRadiusChange = () => {},
  filters = {}
}) => {
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [zoomLevel, setZoomLevel] = useState(12);
  const [isLocating, setIsLocating] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    if (userLocation) {
      setMapCenter(userLocation);
    }
  }, [userLocation]);

  const handleGetCurrentLocation = () => {
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation?.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position?.coords?.latitude,
            lng: position?.coords?.longitude
          };
          setMapCenter(newLocation);
          setZoomLevel(14);
          setIsLocating(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setIsLocating(false);
        }
      );
    } else {
      setIsLocating(false);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 1, 18));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 1, 8));
  };

  const getShopMarkerColor = (shop) => {
    if (selectedShop?.id === shop?.id) return '#3182ce';
    if (shop?.isPartner) return '#38a169';
    return '#718096';
  };

  const getServiceIcon = (services) => {
    if (services?.includes('repair') && services?.includes('sales')) return 'Wrench';
    if (services?.includes('repair')) return 'Settings';
    if (services?.includes('sales')) return 'ShoppingBag';
    return 'MapPin';
  };

  const filteredShops = shops?.filter(shop => {
    if (filters?.services && filters?.services?.length > 0) {
      return filters?.services?.some(service => shop?.services?.includes(service));
    }
    if (filters?.brands && filters?.brands?.length > 0) {
      return filters?.brands?.some(brand => shop?.brands?.includes(brand));
    }
    if (filters?.minRating) {
      return shop?.rating >= filters?.minRating;
    }
    return true;
  });

  return (
    <div className="relative w-full h-full bg-muted rounded-lg overflow-hidden">
      {/* Map Container */}
      <div className="w-full h-full relative">
        <iframe
          ref={mapRef}
          width="100%"
          height="100%"
          loading="lazy"
          title="Bike Shops Near You"
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps?q=${mapCenter?.lat},${mapCenter?.lng}&z=${zoomLevel}&output=embed`}
          className="w-full h-full"
        />

        {/* Map Overlay Controls */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
          {/* Search Radius Control */}
          <div className="bg-card border border-border rounded-lg p-3 shadow-md">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="MapPin" size={16} color="var(--color-accent)" />
              <span className="text-sm font-medium text-foreground">Search Radius</span>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="1"
                max="50"
                value={searchRadius}
                onChange={(e) => onRadiusChange(parseInt(e?.target?.value))}
                className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-sm text-muted-foreground w-12">
                {searchRadius}mi
              </span>
            </div>
          </div>

          {/* Map Controls */}
          <div className="flex flex-col space-y-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleGetCurrentLocation}
              disabled={isLocating}
              className="bg-card shadow-md"
            >
              <Icon 
                name={isLocating ? "Loader2" : "Crosshair"} 
                size={18} 
                className={isLocating ? "animate-spin" : ""}
              />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomIn}
              className="bg-card shadow-md"
            >
              <Icon name="Plus" size={18} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomOut}
              className="bg-card shadow-md"
            >
              <Icon name="Minus" size={18} />
            </Button>
          </div>
        </div>

        {/* Shop Markers Overlay */}
        <div className="absolute inset-0 pointer-events-none">
          {filteredShops?.map((shop) => (
            <div
              key={shop?.id}
              className="absolute pointer-events-auto cursor-pointer transform -translate-x-1/2 -translate-y-1/2"
              style={{
                left: `${50 + (shop?.coordinates?.lng - mapCenter?.lng) * 100}%`,
                top: `${50 + (mapCenter?.lat - shop?.coordinates?.lat) * 100}%`,
              }}
              onClick={() => onShopSelect(shop)}
            >
              <div className={`relative p-2 rounded-full shadow-lg transition-all duration-200 interactive-scale ${
                selectedShop?.id === shop?.id ? 'scale-125' : 'hover:scale-110'
              }`}
              style={{ backgroundColor: getShopMarkerColor(shop) }}>
                <Icon 
                  name={getServiceIcon(shop?.services)} 
                  size={16} 
                  color="white" 
                />
                
                {/* Partner Badge */}
                {shop?.isPartner && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full border-2 border-white" />
                )}
                
                {/* Pulse Animation */}
                {selectedShop?.id === shop?.id && (
                  <div 
                    className="absolute inset-0 rounded-full animate-ping opacity-30"
                    style={{ backgroundColor: getShopMarkerColor(shop) }}
                  />
                )}
              </div>
              
              {/* Shop Name Tooltip */}
              {selectedShop?.id === shop?.id && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-card border border-border rounded text-xs font-medium text-foreground shadow-lg whitespace-nowrap animate-fade-in">
                  {shop?.name}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-card border-r border-b border-border rotate-45" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-card border border-border rounded-lg p-3 shadow-md">
          <h4 className="text-sm font-semibold text-foreground mb-2">Legend</h4>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full" />
              <span className="text-xs text-muted-foreground">Partner Shop</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-muted-foreground rounded-full" />
              <span className="text-xs text-muted-foreground">Regular Shop</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-accent rounded-full" />
              <span className="text-xs text-muted-foreground">Selected</span>
            </div>
          </div>
        </div>

        {/* Shop Count */}
        <div className="absolute bottom-4 right-4 bg-card border border-border rounded-lg px-3 py-2 shadow-md">
          <div className="flex items-center space-x-2">
            <Icon name="Store" size={16} color="var(--color-accent)" />
            <span className="text-sm font-medium text-foreground">
              {filteredShops?.length} shops found
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopMap;